import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sun, Moon, Key, Eye, Plus, PencilSimple, CheckSquare,
  Checks, Trash, ChatCircleDots, Link, CaretLeft,
} from '@phosphor-icons/react';
import '../styles/App.css';

const DASHBOARD_PATH = '/dashboard';

/** Handy Co hand logo, matching the rest of the dashboard */
const HandIcon = ({ size = 40 }) => (
  <div
    role="img"
    aria-label="Handy Co Logo"
    style={{
      width: size,
      height: size,
      backgroundColor: 'var(--crm-text, #9CACA6)',
      WebkitMaskImage: 'url(/assets/icons/icon_handyco.svg)',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskImage: 'url(/assets/icons/icon_handyco.svg)',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      maskSize: 'contain',
      flexShrink: 0,
    }}
  />
);

/** Icon map for activity event types */
const EVENT_ICONS = {
  sign_in: <Key weight="fill" size={14} />,
  page_view: <Eye weight="fill" size={14} />,
  client_created: <Plus weight="bold" size={14} />,
  client_updated: <PencilSimple weight="fill" size={14} />,
  task_created: <CheckSquare weight="fill" size={14} />,
  task_completed: <Checks weight="bold" size={14} />,
  task_deleted: <Trash weight="fill" size={14} />,
  thought_added: <ChatCircleDots weight="fill" size={14} />,
  thought_updated: <ChatCircleDots weight="fill" size={14} />,
  doc_added: <Link weight="bold" size={14} />,
};

/** Human-readable label for each event type */
const EVENT_LABELS = {
  sign_in: 'Signed in',
  page_view: 'Viewed page',
  client_created: 'Created client',
  client_updated: 'Updated client',
  task_created: 'Created task',
  task_completed: 'Completed task',
  task_deleted: 'Deleted task',
  thought_added: 'Added note',
  thought_updated: 'Updated note',
  doc_added: 'Added document',
};

/** Format a Unix-ms timestamp into a readable string */
const formatTime = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/** Group activities by calendar day */
const groupByDay = (activities) => {
  const groups = {};
  for (const act of activities) {
    const d = new Date(act.created_at);
    const key = d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    if (!groups[key]) groups[key] = [];
    groups[key].push(act);
  }
  return Object.entries(groups);
};

/**
 * Profile page — shows the authenticated user's activity log.
 */
const Profile = () => {
  const navigate = useNavigate();

  // Auth state
  const [sessionToken, setSessionToken] = useState('');
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Activity data
  const [activities, setActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [error, setError] = useState('');

  // Theme (sync with other dashboard pages)
  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') || 'dark');

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('crm_theme', next);
  };

  // ============================================
  // AUTH — same pattern as Crm.js / AllClients.js
  // ============================================
  useEffect(() => {
    const storedToken = localStorage.getItem('crm_session');
    const storedUser = localStorage.getItem('crm_user');

    if (!storedToken) {
      navigate(DASHBOARD_PATH);
      return;
    }

    let parsedUser = null;
    try {
      parsedUser = storedUser ? JSON.parse(storedUser) : null;
    } catch {
      /* ignore parse errors */
    }

    // Validate token with the backend
    fetch('/api/crm/auth/session', {
      headers: { Authorization: `Bearer ${storedToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Invalid session');
        return r.json();
      })
      .then((data) => {
        setSessionToken(storedToken);
        setUser(parsedUser || data.user || null);
        setAuthenticated(true);
        setAuthLoading(false);
      })
      .catch(() => {
        localStorage.removeItem('crm_session');
        localStorage.removeItem('crm_user');
        navigate(DASHBOARD_PATH);
      });
  }, [navigate]);

  // ============================================
  // LOAD ACTIVITY
  // ============================================
  useEffect(() => {
    if (!authenticated || !sessionToken) return;
    setActivityLoading(true);

    fetch('/api/crm/activity', {
      headers: { Authorization: `Bearer ${sessionToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Activity API error ${r.status}`);
        return r.json();
      })
      .then((data) => {
        setActivities(data.activities || []);
        setActivityLoading(false);
      })
      .catch((err) => {
        setError(`Failed to load activity: ${err.message}`);
        setActivityLoading(false);
      });

    // Log this page view
    fetch('/api/crm/activity', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${sessionToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ event_type: 'page_view', label: 'Profile', metadata: { path: '/dashboard/profile' } }),
    }).catch(() => {});
  }, [authenticated, sessionToken]);

  // ============================================
  // SIGN OUT
  // ============================================
  const handleSignOut = () => {
    localStorage.removeItem('crm_session');
    localStorage.removeItem('crm_user');
    navigate(DASHBOARD_PATH);
  };

  // Loading / unauthenticated guards
  if (authLoading) {
    return (
      <div className={`crm-page${theme === 'light' ? ' crm-light' : ''}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div className="crm-spinner" />
      </div>
    );
  }

  if (!authenticated) return null;

  // Stats derived from activities
  const totalActions = activities.length;
  const signIns = activities.filter((a) => a.event_type === 'sign_in').length;
  const clientsCreated = activities.filter((a) => a.event_type === 'client_created').length;
  const tasksCompleted = activities.filter((a) => a.event_type === 'task_completed').length;

  const grouped = groupByDay(activities);

  return (
    <div className={`crm-page${theme === 'light' ? ' crm-light' : ''}`}>
      {/* Header */}
      <header className="crm-header">
        <div className="crm-header-brand" onClick={() => navigate(DASHBOARD_PATH)}>
          <HandIcon size={36} />
          <span className="crm-header-title">
            <span className="crm-header-wordmark">HANDY CO</span>
          </span>
        </div>
        <div className="crm-header-actions">
          <button className="crm-button ghost crm-theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            {theme === 'dark' ? <Sun weight="fill" size={18} /> : <Moon weight="fill" size={18} />}
          </button>
          <button className="crm-button ghost" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="crm-main crm-main-full">
        {/* Back link */}
        <div className="crm-detail-nav">
          <button className="crm-button ghost" onClick={() => navigate(DASHBOARD_PATH)}>
            <CaretLeft weight="bold" size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Dashboard
          </button>
        </div>

        {/* Profile hero */}
        <div className="profile-hero">
          {user?.picture && (
            <img src={user.picture} alt={user.name} className="profile-avatar" />
          )}
          <div className="profile-hero-info">
            <h1 className="profile-name">{user?.name || 'Unknown User'}</h1>
            <p className="profile-email">{user?.email}</p>
          </div>
        </div>

        {/* Stats row */}
        <div className="profile-stats">
          <div className="profile-stat">
            <span className="profile-stat-value">{totalActions}</span>
            <span className="profile-stat-label">Total Actions</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{signIns}</span>
            <span className="profile-stat-label">Sign-ins</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{clientsCreated}</span>
            <span className="profile-stat-label">Clients Created</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat-value">{tasksCompleted}</span>
            <span className="profile-stat-label">Tasks Completed</span>
          </div>
        </div>

        {/* Activity feed */}
        <div className="profile-section">
          <h2 className="profile-section-title">Activity</h2>

          {error && <div className="crm-error">{error}</div>}

          {activityLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
              <div className="crm-spinner" />
            </div>
          )}

          {!activityLoading && activities.length === 0 && (
            <p className="profile-empty">No activity recorded yet. Start using the dashboard to see your history here.</p>
          )}

          {!activityLoading && grouped.map(([day, items]) => (
            <div key={day} className="profile-day-group">
              <div className="profile-day-label">{day}</div>
              <div className="profile-activity-list">
                {items.map((act) => (
                  <div key={act.id} className="profile-activity-item">
                    <span className="profile-activity-icon">
                      {EVENT_ICONS[act.event_type] || '•'}
                    </span>
                    <div className="profile-activity-body">
                      <span className="profile-activity-type">
                        {EVENT_LABELS[act.event_type] || act.event_type}
                      </span>
                      {act.label && (
                        <span className="profile-activity-label"> — {act.label}</span>
                      )}
                    </div>
                    <span className="profile-activity-time">{formatTime(act.created_at)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Profile;
